/**
 * LoginPage — the foyer of the residence.
 *
 * Warm ivory canvas. No colour panel. No gradient. No marketing wash.
 * Left column carries the welcome and the form. Right column carries a
 * miniature Morning Brief so the visitor sees the house they are
 * entering, in the same design language.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import {
  AuthShell, UnderlineInput, TextLink, Rule, useLightPhase,
} from "../pages/homeos/components/os/editorial";
import AuthMorningBriefPreview from "../pages/homeos/components/os/AuthMorningBriefPreview";

export default function LoginPage() {
  const { login, loginWithGoogle, demoLogin } = useAuth();
  const navigate = useNavigate();
  const { phase, greeting } = useLightPhase();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome home");
      navigate("/home-os");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = async () => {
    setLoading(true);
    try {
      await demoLogin();
      toast.success("The house is open");
      navigate("/home-os");
    } catch (error) {
      toast.error(error.message || "Demo login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      phase={phase}
      preview={<AuthMorningBriefPreview />}
      testid="login-shell"
    >
      {/* Masthead */}
      <div className="rise-ara">
        <p className="caption-ara">ARA</p>
        <p
          className="mt-3 font-display text-[22px] leading-tight"
          style={{ fontWeight: 400, color: 'var(--ara-ink)', letterSpacing: '-0.01em' }}
          data-testid="auth-brand"
        >
          Household operating system
        </p>
      </div>

      {/* Welcome */}
      <div className="mt-16 rise-ara rise-ara-s1">
        <h1
          className="display-ara text-[48px] sm:text-[64px] lg:text-[72px] leading-[1.02] max-w-[12ch]"
          data-testid="login-title"
        >
          Welcome home.
        </h1>
        <p className="mt-8 prose-ara max-w-[42ch] text-[17px]">
          Sign in to your residence. Everything is exactly where you left it —
          {greeting ? ` and the house says ${greeting.toLowerCase()}.` : ' and the house is ready for you.'}
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="mt-16 space-y-10 rise-ara rise-ara-s2" data-testid="login-form">
        <UnderlineInput
          id="email"
          label="Email"
          type="email"
          placeholder="you@residence"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          required
          testid="email-input"
        />
        <UnderlineInput
          id="password"
          label="Password"
          type="password"
          placeholder="•••••••••"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          required
          testid="password-input"
        />

        <div className="pt-4 flex flex-wrap items-center gap-x-6 gap-y-4">
          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit-btn"
            className="inline-flex items-center gap-2 rounded-full px-6 py-3 transition-opacity hover:opacity-80 disabled:opacity-40"
            style={{
              background: 'var(--ara-ink)',
              color: 'var(--ara-surface)',
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: '12px',
              fontWeight: 500,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              minHeight: '44px',
            }}
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Opening the door</>
            ) : (
              <>Enter the residence &nbsp;→</>
            )}
          </button>

          <button
            type="button"
            onClick={loginWithGoogle}
            data-testid="google-login-btn"
            className="link-ara"
            style={{ fontSize: '13px', letterSpacing: '0.06em', color: 'var(--ara-ink-muted)' }}
          >
            Continue with Google
          </button>
        </div>
      </form>

      {/* Soft divider */}
      <div className="mt-16 rise-ara rise-ara-s3">
        <Rule soft />
      </div>

      {/* Guest & register */}
      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-y-6 gap-x-10 rise-ara rise-ara-s4">
        <div>
          <p className="caption-ara">Simply visiting?</p>
          <button
            type="button"
            onClick={handleDemoLogin}
            disabled={loading}
            data-testid="demo-login-btn"
            className="link-ara mt-3 inline-flex disabled:opacity-40"
            style={{ fontSize: '14px', letterSpacing: '0.04em' }}
          >
            Step in as a guest
          </button>
        </div>
        <div>
          <p className="caption-ara">New to the residence?</p>
          <Link
            to="/register"
            data-testid="register-link"
            className="link-ara mt-3 inline-flex"
            style={{ fontSize: '14px', letterSpacing: '0.04em' }}
          >
            Set up a new household
          </Link>
        </div>
      </div>

      {/* Colophon */}
      <div className="mt-24 rise-ara rise-ara-s5">
        <Rule soft />
        <p className="caption-ara mt-6">
          ARA · Set in Fraunces &amp; Inter · Printed on warm ivory
        </p>
      </div>
    </AuthShell>
  );
}
